// react
import { useCallback, useEffect, useState } from 'react';
// @mui
import {
  DataGridPremium,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GRID_AGGREGATION_FUNCTIONS,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
  GridColumnVisibilityModel,
  GridRowGroupingModel,
  GridSortModel,
  GridFilterModel,
  GridColumnOrderChangeParams,
  GridAggregationModel,
  GridColumnResizeParams,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridCallbackDetails,
} from '@mui/x-data-grid-premium';
// dto
import { ReportRowDto, ReportColumnDto, ReportDataSource } from 'src/api';
// types
import { ReportTemplateContainer } from 'src/types/report-template';
// locales
import { useLocales } from 'src/locales';
// redux
import { useDispatch, useSelector } from 'src/redux/store';
import {
  updateColumns,
  setColumnVisibilityModel,
  setRowGroupingModel,
  setSortModel,
  setFilterModel,
  setCustomWidth,
  setAggregationModel,
  setPageSize,
  setIntervalModel,
  removeReportTabel,
  setDescription,
  setPinnedModel,
} from 'src/redux/slices/report';
// utils
import { arrayMove, arrayUpdateIndex, uniqueEntry } from 'src/utils/array-utils';
// components
import { ReportTableToolbar } from './report-table-toolbar';
// calculation
import { Attendance } from './calculation/attendance';
import { Churn } from './calculation/churn';
import { ConversionRate } from './calculation/conversion-rate';
import { LEG } from './calculation/leg';
import { DeviationFromAverage } from './calculation/deviation-from-average';
import { Count } from './calculation/count';
// columns
import { ColumnTemplates } from './column';
// constants
import { classRegistrationsColumns } from './constants/data-source/class-registrations/columns';
import { classesColumns } from './constants/data-source/classes/columns';
import { membershipsColumns } from './constants/data-source/memberships/columns';
import { orderItemColumns } from './constants/data-source/order-items/columns';
import { memberColumns } from './constants/data-source/member/columns';
import { RelativeSize } from './calculation/relative-size';
import { TotalAverage } from './calculation/total-average';
// injector
import { VariableInjector } from './variable-injector/variable-injector';

type props = {
  rows: ReportRowDto[];
  columns: ReportColumnDto<any, any, any>[];
  table: ReportTemplateContainer;
  index?: number;
  disableEdit?: boolean;
  interval?: number;
};

export function ReportTable({
  rows,
  columns,
  table,
  index = 0,
  disableEdit = false,
  interval,
}: props) {
  const { t } = useLocales();

  const loading = useSelector((state) => state.report.loading[interval ?? 0]);
  const report = useSelector((state) => state.report.report);

  const [columnVisibilityModel, setLocalColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>();

  const columnTemplates = ColumnTemplates();

  const [baseValueOptions] = useState<({ [key: string]: string } | undefined)[]>([
    ...columnTemplates
      .map(
        (x) =>
          x.valueOptions &&
          x.valueOptions.map((y) => ({
            [t(y)]: y,
          }))
      )
      .flat()
      .filter((a) => a)
      .filter(uniqueEntry),
  ]);

  const dispatch = useDispatch();

  const apiRef = useGridApiRef();

  const deviationFromAverage = DeviationFromAverage({ apiRef });
  const relativeSize = RelativeSize({ apiRef });
  const totalAverage = TotalAverage({ apiRef });

  const handleColumnVisibilityModelChange = (model: GridColumnVisibilityModel) => {
    dispatch(setColumnVisibilityModel({ columnVisibilityModel: model, index }));
  };

  const handleRowGroupingModelChange = (model: GridRowGroupingModel) => {
    dispatch(setRowGroupingModel({ rowGroupingModel: model, index }));
  };

  const handleSortModelChange = (model: GridSortModel) => {
    dispatch(setSortModel({ sortModel: model, index }));
  };

  const handleFilterModelChange = (model: GridFilterModel) => {
    dispatch(
      setFilterModel({
        filterModel: {
          ...model,
          items: model.items.map((x) => ({
            ...x,
            baseValue: baseValueOptions.find((b) => Object.keys(b ?? {})[0] === x.value)?.[x.value],
          })),
        },
        index,
      })
    );
  };

  const handleColumnOrderChange = (params: GridColumnOrderChangeParams) => {
    const { field } = params.column;
    const { targetIndex } = params;

    const i = table.columns?.findIndex((x) => x.field === field);

    if (i && i !== -1 && table.columns !== undefined) {
      const newColumns = arrayUpdateIndex(arrayMove([...(table.columns ?? [])], i, targetIndex));

      dispatch(updateColumns({ columns: newColumns, index }));
    }
  };

  const handleAggregationModelChange = (model: GridAggregationModel) => {
    dispatch(setAggregationModel({ aggregationModel: model, index }));
  };

  const handleIntervalChange = (value?: number) => {
    if (index !== undefined) {
      if (value === undefined) {
        dispatch(setIntervalModel({ intervalModel: [], index }));
      } else {
        dispatch(setIntervalModel({ intervalModel: [value], index }));
      }
    }
  };

  const handleDescriptionChange = (value: string) => {
    if (index !== undefined) {
      dispatch(setDescription({ description: value, index }));
    }
  };

  const handlePinnedColumnsChange = (
    pinnedColumns: GridPinnedColumnFields,
    details: GridCallbackDetails<any>
  ) => {
    if (!disableEdit) {
      if (index !== undefined) {
        dispatch(setPinnedModel({ pinnedModel: pinnedColumns, index }));
      }
    }
  };

  useEffect(() => {
    setLocalColumnVisibilityModel(table.columnVisibilityModel);
  }, [table.columnVisibilityModel]);

  useEffect(() => {
    if (rows.length !== 0) {
      let tempColumnVisibilityModel: GridColumnVisibilityModel = {};

      tempColumnVisibilityModel = {
        ...tempColumnVisibilityModel,
        ...membershipsColumns
          .map((x) => x)
          .reduce(
            (o, key) =>
              Object.assign(o, {
                [key]:
                  tempColumnVisibilityModel[key] !== true
                    ? report.dataSource?.some((x) => x === ReportDataSource.Memberships)
                    : true,
              }),
            {}
          ),
      };

      tempColumnVisibilityModel = {
        ...tempColumnVisibilityModel,
        ...memberColumns
          .map((x) => x)
          .reduce(
            (o, key) =>
              Object.assign(o, {
                [key]:
                  tempColumnVisibilityModel[key] !== true
                    ? report.dataSource?.some((x) => x === ReportDataSource.Member)
                    : true,
              }),
            {}
          ),
      };

      tempColumnVisibilityModel = {
        ...tempColumnVisibilityModel,
        ...classesColumns
          .map((x) => x)
          .reduce(
            (o, key) =>
              Object.assign(o, {
                [key]:
                  tempColumnVisibilityModel[key] !== true
                    ? report.dataSource?.some((x) => x === ReportDataSource.Classes)
                    : true,
              }),
            {}
          ),
      };

      tempColumnVisibilityModel = {
        ...tempColumnVisibilityModel,
        ...classRegistrationsColumns
          .map((x) => x)
          .reduce(
            (o, key) =>
              Object.assign(o, {
                [key]:
                  tempColumnVisibilityModel[key] !== true
                    ? report.dataSource?.some((x) => x === ReportDataSource.ClassRegistrations)
                    : true,
              }),
            {}
          ),
      };

      tempColumnVisibilityModel = {
        ...tempColumnVisibilityModel,
        ...orderItemColumns
          .map((x) => x)
          .reduce(
            (o, key) =>
              Object.assign(o, {
                [key]:
                  tempColumnVisibilityModel[key] !== true
                    ? report.dataSource?.some((x) => x === ReportDataSource.OrderItems)
                    : true,
              }),
            {}
          ),
      };

      setLocalColumnVisibilityModel({
        ...tempColumnVisibilityModel,
        ...table.columnVisibilityModel,
      });
    }
  }, [rows, report.dataSource, table.columnVisibilityModel]);

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      sorting: {
        sortModel: [{ field: GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, sort: 'asc' }],
      },
      aggregation: {
        model: {
          ...table.aggregationModel,
        },
      },
      pagination: { paginationModel: { pageSize: table.pageSize ?? 25 } },
    },
  });

  const handleFilterModelInjector = useCallback(() => {
    VariableInjector(index);
  }, [index]);

  handleFilterModelInjector();

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const { field } = params.colDef;
    const { width } = params;

    if (field === '__row_group_by_columns_group__' && table.rowGroupingModel && !disableEdit) {
      const columnIndex =
        table.columns?.findIndex((x) => x.field === table.rowGroupingModel![0]) ?? -1;

      if (columnIndex === -1) {
        return;
      }

      dispatch(setCustomWidth({ columnIndex, width, index }));
    } else {
      const columnIndex = table.columns?.findIndex((x) => x.field === field) ?? -1;

      if (columnIndex === -1) {
        return;
      }

      dispatch(setCustomWidth({ columnIndex, width, index }));
    }
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    dispatch(setPageSize({ pageSize: model.pageSize, index }));
  };

  const handleRemoveTable = () => {
    dispatch(removeReportTabel({ index }));
  };

  const renderToolbar = () => (
    <ReportTableToolbar
      removeTable={handleRemoveTable}
      index={index}
      disableEdit={disableEdit}
      intervalModel={disableEdit ? [interval ?? -1] : report.tables![index]?.intervalModel ?? []}
      onIntervalChange={handleIntervalChange}
      onDescriptionChange={handleDescriptionChange}
    />
  );

  return (
    <DataGridPremium
      sx={{
        maxWidth: 2000,
      }}
      pagination
      pageSizeOptions={[10, 25, 50, 100, 300, { value: -1, label: t('All') }]}
      onPaginationModelChange={handlePaginationModelChange}
      rows={
        rows && rows.length > 0
          ? [
              ...rows.map((x) => {
                const { membershipLength } = x;
                if (x && x.membershipLength) {
                  return {
                    ...x,
                    membershipLength: {
                      value: membershipLength,
                      name: `${membershipLength} ${membershipLength === '1' ? 'Day' : 'Days'}`,
                    },
                  };
                }
                return x;
              }),
            ]
          : []
      }
      columns={
        [
          ...columns.map((x) => ({
            ...x,
            headerName: t(columnTemplates.find((y) => y.field === x.field)?.headerName ?? ''),
            description: t(columnTemplates.find((y) => y.field === x.field)?.description ?? ''),
            valueOptions: columnTemplates.find((y) => y.field === x.field)?.valueOptions
              ? columnTemplates.find((y) => y.field === x.field)?.valueOptions?.map((v) => t(v))
              : undefined,
            type: columnTemplates.find((y) => y.field === x.field)?.type,
          })),
        ] as any
      }
      apiRef={apiRef}
      loading={loading}
      disableRowSelectionOnClick
      initialState={initialState}
      filterModel={
        {
          ...table.filterModel,
          items: [
            ...(table.filterModel?.items ?? []).map(
              (x) =>
                ({
                  ...x,
                  value: x.value !== 'true' && x.value !== 'false' ? t(x.value!) : x.value,
                } as any)
            ),
          ],
        } as any
      }
      columnVisibilityModel={{ ...columnVisibilityModel, ...{ id: false, ownerId: false } }}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      rowGroupingModel={table.rowGroupingModel}
      onRowGroupingModelChange={handleRowGroupingModelChange}
      sortModel={table.sortModel}
      onSortModelChange={handleSortModelChange}
      onFilterModelChange={handleFilterModelChange}
      onColumnWidthChange={handleColumnWidthChange}
      slots={{ toolbar: renderToolbar }}
      aggregationFunctions={{
        ...GRID_AGGREGATION_FUNCTIONS,
        Attendance: { ...Attendance, label: t(Attendance.label ?? '') },
        Churn: { ...Churn, label: t(Churn.label ?? '') },
        ConversionRate: { ...ConversionRate, label: t(ConversionRate.label ?? '') },
        LEG: { ...LEG, label: t(LEG.label ?? '') },
        DeviationFromAverage: {
          ...deviationFromAverage,
          label: t(deviationFromAverage.label ?? ''),
        },
        Count: { ...Count, label: t(Count.label ?? '') },
        RelativeSize: { ...relativeSize, label: t(relativeSize.label ?? '') },
        TotalAverage: { ...totalAverage, label: t(totalAverage.label ?? '') },
      }}
      onAggregationModelChange={handleAggregationModelChange}
      onColumnOrderChange={handleColumnOrderChange}
      showToolbar
      pinnedColumns={table.pinnedModel}
      onPinnedColumnsChange={handlePinnedColumnsChange}
      disablePivoting
    />
  );
}
